/**
 * Small, typed `fetch` wrapper for calling this project's own `/api/*`
 * serverless functions.
 *
 * Centralizing this logic (JSON headers, response parsing, error shape)
 * keeps consumers like `ContactForm.tsx` simple and consistent, and makes it
 * easy to reuse for future endpoints — see
 * `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md` §6.
 */

export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

async function parseJsonSafe(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

/**
 * POST a JSON body to a same-origin API route and parse the JSON response.
 * Throws `ApiError` (with `status`/`body`) for non-2xx responses or network
 * failures, so callers can branch on a single `catch`.
 */
export async function apiPost<TResponse = unknown, TBody = unknown>(
    path: string,
    body: TBody,
): Promise<TResponse> {
    let response: Response;
    try {
        response = await fetch(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch (error) {
        throw new ApiError('Network request failed', 0, error);
    }

    const data = await parseJsonSafe(response);

    if (!response.ok) {
        throw new ApiError(`Request to ${path} failed with status ${response.status}`, response.status, data);
    }

    return data as TResponse;
}
